import type { AudioCaptureOptions, LocalParticipant, TrackPublishOptions } from 'livekit-client'
import type { VoiceAudioProcessor } from './voice-audio'

/** createTracks assigns the participant's audio context after capture. Attach
 * processing at that point, before publishing any microphone audio to peers. */
export async function publishProcessedMicrophone(
  participant: LocalParticipant, capture: AudioCaptureOptions, publish: TrackPublishOptions,
  processor: VoiceAudioProcessor, isActive: () => boolean,
) {
  const tracks = await participant.createTracks({ audio: capture, video: false })
  try {
    const track = tracks.find((t) => t.kind === 'audio')
    if (!track || track.kind !== 'audio') throw new Error('No microphone track was acquired.')
    if (!isActive()) throw new DOMException('Microphone startup was cancelled.', 'AbortError')
    await track.setProcessor(processor)
    if (!isActive()) throw new DOMException('Microphone startup was cancelled.', 'AbortError')
    const publication = await participant.publishTrack(track, publish)
    if (!isActive()) {
      await participant.unpublishTrack(track)
      throw new DOMException('Microphone startup was cancelled.', 'AbortError')
    }
    return publication
  } catch (e) {
    tracks.forEach((track) => track.stop())
    await processor.destroy()
    throw e
  }
}

export function microphoneError(error: unknown): string {
  const e = error as { name?: string; message?: string } | null
  switch (e?.name) {
    case 'NotAllowedError': return 'Microphone access was denied. Allow microphone access in your browser’s site settings.'
    case 'NotFoundError': return 'No microphone was found. Connect one or choose another input device.'
    case 'NotReadableError': return 'The microphone is unavailable. Check whether another app is using it.'
    case 'OverconstrainedError': return 'The selected microphone cannot use these settings. Choose another input device.'
    default: return 'Microphone could not start. ' + (e?.message || 'Try again or choose another input device.')
  }
}
